import { Injectable } from '@angular/core';
import { Action, NgxsOnInit, State, StateContext } from '@ngxs/store';
import { insertItem, patch } from '@ngxs/store/operators';

import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { GetBotScenarios } from '../../../core/store/bot/bot.actions';
import { ChatService } from '../services/chat.service';
import {
  ChatApiResponse,
  ChatStateModel,
  Suggestion,
} from './chat-state.model';
import {
  ChangeDialogMode,
  GetMessages,
  GetSuggestions,
  PushNewMessage,
  RunScenario,
  SendFile,
  SendMessage,
  SetClient,
  SwitchInfoMode,
} from './chat.actions';

@State<ChatStateModel>({
  name: 'chat',
})
@Injectable()
export class ChatState implements NgxsOnInit {
  constructor(private chatService: ChatService) {}

  ngxsOnInit({ patchState }: StateContext<ChatStateModel>): void {
    patchState({ status: 'true', infoMode: this.infoMode });
  }

  // ------------------------------ Messages --------------------------

  @Action(GetMessages)
  getMessages({
    getState,
    patchState,
  }: StateContext<ChatStateModel>): Observable<ChatApiResponse> | undefined {
    const status = getState().status;
    if (status === 'full' || status === 'pending') {
      return;
    }

    patchState({ status: 'pending' });

    const client = getState().client;
    const offset = getState().messages.length;

    return this.chatService.getMessages(client, offset).pipe(
      tap((res) => {
        if (!res.messages.length) {
          patchState({ status: 'full' });
          return;
        }
        patchState({
          messages: [...getState().messages, ...res.messages.reverse()],
          status: 'true',
        });
      })
    );
  }

  @Action(SendMessage)
  sendMessage(
    { getState, patchState }: StateContext<ChatStateModel>,
    { message }: SendMessage
  ): Observable<ChatStateModel> {
    const clientId = getState().client.client_id;

    return this.chatService.sendMessage(clientId, message).pipe(
      tap(() => {
        patchState({
          suggestions: [],
        });
      })
    );
  }

  @Action(SendFile)
  sendFile(
    { getState }: StateContext<ChatStateModel>,
    { file, type }: SendFile
  ): void {
    const clientId = getState().client.client_id;
    return this.chatService.sendFile(file, type, clientId);
  }

  @Action(RunScenario)
  runScenario(
    { getState }: StateContext<ChatStateModel>,
    { scenarioId }: RunScenario
  ): void {
    const clientId = getState().client.client_id;
    return this.chatService.runScenario(scenarioId, clientId);
  }

  @Action(PushNewMessage)
  pushNewMessage(
    { setState }: StateContext<ChatStateModel>,
    { message }: PushNewMessage
  ): void {
    setState(
      patch({
        messages: insertItem(message),
      })
    );
  }

  @Action(GetSuggestions, { cancelUncompleted: true })
  getSuggestions(
    { patchState }: StateContext<ChatStateModel>,
    { keyword }: GetSuggestions
  ): Observable<Suggestion[]> | void {
    if (!keyword) {
      patchState({
        suggestions: [],
      });
      return;
    }
    return this.chatService.getSuggestions(keyword).pipe(
      tap((suggestions) => {
        patchState({
          suggestions,
        });
      })
    );
  }

  // ------------------------------- Client ---------------------------

  @Action(SetClient)
  setClient(
    { patchState, dispatch }: StateContext<ChatStateModel>,
    { client }: SetClient
  ): void {
    patchState({
      client,
      messages: [],
      status: 'true',
    });

    dispatch([new GetMessages(), new GetBotScenarios()]);
  }

  // ------------------------------ Chat --------------------------

  @Action(ChangeDialogMode)
  changeDialogMode({
    getState,
    patchState,
  }: StateContext<ChatStateModel>): any {
    const client = getState().client;
    return this.chatService.changeDialogMode(client).pipe(
      tap((mode) => {
        patchState({
          client: { ...client, mode },
        });
      })
    );
  }

  @Action(SwitchInfoMode)
  switchInfoMode({ getState, patchState }: StateContext<ChatStateModel>): void {
    const mode = getState().infoMode;
    this.setInfoMode(!mode);
    patchState({
      infoMode: !mode,
    });
  }

  // ---------------------------- LocalStore --------------------------

  private get infoMode(): boolean {
    // 'im' <- Info mode
    const mode = localStorage.getItem('im');
    if (!mode) {
      localStorage.setItem('im', '0');
      return false;
    }
    return mode === '1' ? true : false;
  }

  private setInfoMode(mode: boolean): void {
    const newMode = mode ? '1' : '0';
    localStorage.setItem('im', newMode);
  }
}
